import { ReactNode } from 'react';

import {
  Box,
  Button,
  Divider,
  Icons,
  LayoutCard,
  RowInset,
  Stack,
  toDsVarsDefinition,
  Bleed,
  tokens,
} from '@cast/design-system';

import { Stepper } from './Stepper';

type Props = {
  title: string;
  /** @default 'A1' */
  titleVariant?: 'A1' | 'h5';
  totalSteps?: number;
  currentStep?: number;
  onBack?: () => void;
  headerEndContent?: ReactNode;
  actions?: ReactNode;
  /** Stretch card to fill the parent. Use for dialog layout only. */
  fillHeight?: boolean;
  /** Flush the right slot to the card bottom (no bottom padding). */
  flushRightBottom?: boolean;
  slots: { left: ReactNode; right: ReactNode };
};

const ONBOARDING_CARD_HEIGHT = 640;
/** Connect-cluster modal — slightly wider/taller than legacy card defaults. */
export const ONBOARDING_MODAL_MAX_WIDTH = 1280;
export const ONBOARDING_MODAL_HEIGHT = 760;
/** Page mode: wider than modal, capped below full-bleed ultrawide. */
export const ONBOARDING_PAGE_CARD_MAX_WIDTH = 1600;
const ONBOARDING_PAGE_CARD_HEIGHT = 'min(800px, calc(100dvh - 168px))';

export const CardShell = ({
  title,
  titleVariant = 'A1',
  actions,
  onBack,
  totalSteps,
  currentStep,
  headerEndContent,
  fillHeight = false,
  flushRightBottom = false,
  slots: { left, right },
}: Props) => {
  const defaultHeaderEndContent =
    totalSteps != null && currentStep != null ? (
      <Stack direction="row" gap={8} alignItems="center">
        {onBack ? (
          <>
            <Button
              variant="ghost"
              size="sm"
              startIcon={<Icons.ArrowLeft size={16} />}
              onClick={onBack}
            >
              Back
            </Button>
            <Divider orientation="vertical" sx={{ height: 20 }} />
          </>
        ) : null}
        <Stepper currentStep={currentStep} totalSteps={totalSteps} />
      </Stack>
    ) : null;

  return (
    <LayoutCard
      title={title}
      headerEndContent={headerEndContent ?? defaultHeaderEndContent}
      sx={{ width: '100%' }}
      cssVars={toDsVarsDefinition({
        inset: { pl: '24px', pr: '24px', pb: '0px' },
      })}
      slotProps={{
        title: { variant: titleVariant },
        header: { dividerBottom: true },
        content: {
          pr: false,
          pb: false,
          ...(fillHeight
            ? {
                sx: {
                  flex: 1,
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                },
              }
            : {}),
        },
        ...(fillHeight
          ? {
              root: {
                sx: {
                  flex: 1,
                  minHeight: 0,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                },
              },
            }
          : {}),
      }}
    >
      <Stack
        direction="row"
        alignItems="stretch"
        sx={
          fillHeight
            ? { flex: 1, minHeight: 0, height: '100%', width: '100%' }
            : {
                height: ONBOARDING_PAGE_CARD_HEIGHT,
                minHeight: ONBOARDING_CARD_HEIGHT,
                width: '100%',
              }
        }
      >
        <Stack
          sx={{
            flexShrink: 0,
            alignSelf: 'stretch',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            my={tokens.primitives.space[600]}
            sx={
              fillHeight
                ? { flex: 1, minHeight: 0, overflow: 'auto' }
                : { overflow: 'auto' }
            }
          >
            {left}
          </Box>
          <Bleed marginTop="auto">
            <RowInset startContent={actions} pb={24} />
          </Bleed>
        </Stack>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            alignSelf: 'stretch',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
            pb: flushRightBottom ? 0 : 24,
          }}
        >
          {right}
        </Box>
      </Stack>
    </LayoutCard>
  );
};
